import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { OracleResult } from '../types/oracle';
import { HistoryManager, QuestionHistoryEntry } from '../lib/historyManager';

export default function QuestionDetail() {
  const { id } = useParams<{ id: string }>();
  const [response, setResponse] = useState<OracleResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [allAnswers, setAllAnswers] = useState<Record<string, string[]>>({});
  const [questionHistory, setQuestionHistory] = useState<QuestionHistoryEntry[]>([]);
  const [includeIrrelevant, setIncludeIrrelevant] = useState(() => {
    const stored = localStorage.getItem('include_irrelevant_options');
    return stored ? JSON.parse(stored) : false;
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize state from history if available and handle browser back/forward
  useEffect(() => {
    const historyState = location.state as { 
      answers?: Record<string, string[]>;
      questionHistory?: Array<{ fieldId: string; selected: string[] }>;
    } | null;
    
    if (historyState?.answers) {
      setAllAnswers(historyState.answers);
      if (historyState.questionHistory) {
        setQuestionHistory(historyState.questionHistory);
        
        // Re-fetch question with reconstructed answers when using browser navigation
        const fetchQuestion = async () => {
          try {
            const response = await fetch(`/api/quiz/${id}?include_irrelevant_options=${includeIrrelevant}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(historyState.answers),
            });

            if (!response.ok) {
              throw new Error('Failed to fetch question after navigation');
            }

            const data: OracleResult = await response.json();
            if (data.type === 'result') {
              localStorage.setItem(`quizmatron_result_${id}`, JSON.stringify(data));
              navigate(`/result/${id}`, { state: { result: data } });
              return;
            }

            setResponse(data);
            // Restore selected answers if they match the current question
            const currentQuestion = historyState.questionHistory ? 
              historyState.questionHistory[historyState.questionHistory.length - 1] : 
              null;
            if (currentQuestion && data.field_id === currentQuestion.fieldId) {
              setSelectedAnswers(currentQuestion.selected);
            } else {
              setSelectedAnswers([]);
            }
          } catch (err) {
            setError('Failed to restore previous question');
            console.error(err);
          }
        };

        fetchQuestion();
      }
    }
  }, [location.key, id, includeIrrelevant]); // Watch location.key to detect navigation changes

  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        // Get answers from either state or history
        const historyState = location.state as { answers?: Record<string, string[]> } | null;
        const currentAnswers = historyState?.answers || allAnswers;
        
        const response = await fetch(`/api/quiz/${id}?include_irrelevant_options=${includeIrrelevant}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(currentAnswers),
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch question');
        }
        
        const data: OracleResult = await response.json();
        if (data.type === 'result') {
          // If we get a result on initial load, navigate to result page
          localStorage.setItem(`quizmatron_result_${id}`, JSON.stringify(data));
          navigate(`/result/${id}`, { state: { result: data } });
          return;
        }
        setResponse(data);
      } catch (err) {
        setError('Failed to load question');
        console.error(err);
      }
    };

    fetchQuestion();
  }, [id]);

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!response) {
    return <div>Loading...</div>;
  }

  if (response.type !== 'question') {
    return <div className="error">Invalid question data</div>;
  }
  
  const question = response.value;

  return (
    <>
      <div className="question-detail">
      <div className="navigation">
        <button
          onClick={async () => {
            if (questionHistory.length > 0) {
              // Remove the last question from history
              const newHistory = questionHistory.slice(0, -1);
              const newAnswers = HistoryManager.reconstructAnswersFromHistory(newHistory);
              
              try {
                // Re-fetch the question with the reconstructed answers
                const response = await fetch(`/api/quiz/${id}?include_irrelevant_options=${includeIrrelevant}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(newAnswers),
                });

                if (!response.ok) {
                  throw new Error('Failed to fetch question after going back');
                }

                const data: OracleResult = await response.json();
                if (data.type === 'result') {
                  localStorage.setItem(`quizmatron_result_${id}`, JSON.stringify(data));
                  navigate(`/result/${id}`, { state: { result: data } });
                  return;
                }

                // Update all state after successful fetch
                setResponse(data);
                setAllAnswers(newAnswers);
                setQuestionHistory(newHistory);
                setSelectedAnswers([]);
              } catch (err) {
                setError('Failed to go back to previous question');
                console.error(err);
              }
            }
          }}
          disabled={questionHistory.length === 0}
        >
          Back
        </button>
      </div>
      <div className="settings">
        <label>
          <input
            type="checkbox"
            checked={includeIrrelevant}
            onChange={(e) => {
              const newValue = e.target.checked;
              setIncludeIrrelevant(newValue);
              localStorage.setItem('include_irrelevant_options', JSON.stringify(newValue));
            }}
          />
          Show all options (including irrelevant ones)
        </label>
      </div>
      <h1>{question.question}</h1>
      <div className="options">
        {[
          ...Object.entries(question.options)
            .map(([value, option]) => ({
              value,
              option,
              isNone: false
            }))
            .sort((a, b) => a.option.label.localeCompare(b.option.label)),
          ...(!question.multiple ? [{
            value: '',
            option: {
              label: 'None of the above',
              description: 'Select this if none of the other options apply'
            },
            isNone: true
          }] : [])
        ].map(({value, option, isNone}, index) => (
          <div key={index} className="option" title={option.description}>
            <input
              type={question.multiple ? 'checkbox' : 'radio'}
              id={`option-${index}`}
              name="question-option"
              value={value}
              checked={isNone ? selectedAnswers.length === 0 : selectedAnswers.includes(value)}
              onChange={(e) => {
                if (question.multiple) {
                  setSelectedAnswers(prev => 
                    e.target.checked 
                      ? [...prev, value] 
                      : prev.filter(v => v !== value)
                  );
                } else {
                  setSelectedAnswers(isNone ? [] : [value]);
                }
              }}
            />
            <label htmlFor={`option-${index}`}>
              {option.label}
              {option.description && <span className="tooltip-icon">?</span>}
            </label>
          </div>
        ))}
      </div>
      <button 
        className="next-button"
        onClick={async () => {
          try {
            const currentAnswers: Record<string, string[]> = { [response.field_id]: selectedAnswers };
            const updatedAnswers: Record<string, string[]> = { ...allAnswers, ...currentAnswers };
            
            // Update history state with new answers and question history
            const updatedHistory = HistoryManager.addAnswer(
              questionHistory,
              response.field_id,
              selectedAnswers
            );
            navigate(location.pathname, { 
              state: { 
                answers: updatedAnswers,
                questionHistory: updatedHistory
              }
            });
            const apiResponse = await fetch(`/api/quiz/${id}?include_irrelevant_options=${includeIrrelevant}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(updatedAnswers),
            });
            
            if (!apiResponse.ok) {
              throw new Error('Failed to submit answer');
            }
            
            const data: OracleResult = await apiResponse.json();
            
            if (data.type === 'result') {
              // Persist result data so that Safari won’t lose it
              localStorage.setItem(`quizmatron_result_${id}`, JSON.stringify(data));
              navigate(`/result/${id}`, { state: { result: data } });
            } else {
              setResponse(data);
              setAllAnswers(updatedAnswers);
              setSelectedAnswers([]);
            }
          } catch (err) {
            setError('Failed to submit answer');
            console.error(err);
          }
        }}
        disabled={false}
      >
        Next
      </button>
      </div>
      <div className="debug-box">
      <h3>Debug Info</h3>
      <pre>{JSON.stringify({
        currentQuestion: question.question,
        selectedAnswers,
        allAnswers
      }, null, 2)}</pre>
      </div>
    </>
  );
}
